import type { ReactElement } from 'react';
import { Download } from 'lucide-react';
import { formatCurrency, formatDate } from '@xdocs/i18n';
import type { Invoice, InvoiceStatus } from '@xdocs/schemas';
import { Badge, DataTable, EmptyState, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { useInvoices } from './useInvoices.js';
import { useSettingsBillingT } from './useSettingsBillingT.js';
import styles from './InvoicesTable.module.css';

const STATUS_TONE: Record<InvoiceStatus, BadgeTone> = {
  paid: 'success',
  open: 'warning',
  void: 'neutral',
  refunded: 'info',
};

export interface InvoicesTableProps {
  /** F26: "Free users don't have invoices — show upgrade context" instead
   * of an empty/populated table. */
  isPro: boolean;
}

/** F26 invoices table — `billing.invoices.list`, PRO-only. */
export function InvoicesTable({ isPro }: InvoicesTableProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();
  const invoices = useInvoices(isPro);

  const columns: ColumnDef<Invoice>[] = [
    {
      id: 'issuedAt',
      header: t('settingsBilling:invoices.column.date'),
      sortable: true,
      primary: true,
      cell: (row) => formatDate(locale, row.issuedAt, { dateStyle: 'medium' }),
    },
    {
      id: 'amount',
      header: t('settingsBilling:invoices.column.amount'),
      cell: (row) => formatCurrency(locale, row.amount, row.currency),
    },
    {
      id: 'status',
      header: t('settingsBilling:invoices.column.status'),
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`settingsBilling:invoices.status.${row.status}`)}</Badge>,
    },
    {
      id: 'download',
      header: t('settingsBilling:invoices.column.download'),
      // GAP (WIRING.md): `invoiceFileId` is a `files` row id, not a URL —
      // there is no known op/route in this task's ownership that resolves
      // it to a presigned download link (F26 AC: "download link expiry"
      // implies a presigned S3 URL, not a static path). `/files/{id}` is a
      // placeholder pending that resolution mechanism.
      cell: (row) =>
        row.invoiceFileId ? (
          <a href={`/files/${row.invoiceFileId}`} target="_blank" rel="noreferrer" className={cn(styles['downloadLink'])}>
            <Download size={14} aria-hidden="true" /> {t('settingsBilling:invoices.download')}
          </a>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <section className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settingsBilling:invoices.title')}</h2>

      {!isPro ? (
        <EmptyState kind="first-use" title={t('settingsBilling:invoices.lockedForFree.title')} description={t('settingsBilling:invoices.lockedForFree.description')} />
      ) : (
        <DataTable
          columns={columns}
          rows={invoices.rows}
          rowKey={(row) => row.id}
          query={invoices.query}
          onQueryChange={invoices.onQueryChange}
          label={t('settingsBilling:invoices.title')}
          isLoading={invoices.isLoading}
          isError={invoices.isError}
          onRetry={invoices.onRetry}
          total={invoices.total}
          nextCursor={invoices.nextCursor}
          onLoadMore={invoices.onLoadMore}
          isLoadingMore={invoices.isLoadingMore}
          {...(invoices.loadMoreError ? { loadMoreError: invoices.loadMoreError } : {})}
          emptyTitle={t('settingsBilling:invoices.empty.title')}
          noResultsTitle={t('settingsBilling:invoices.noResults.title', { query: invoices.query.q ?? '' })}
          onClearFilters={() => {
            const { q: _q, ...rest } = invoices.query;
            invoices.onQueryChange({ ...rest, page: 1 });
          }}
        />
      )}
    </section>
  );
}
